// React
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Servisni sloj
import { getPricingKeysAPI } from "services/api/pricing/api";
import { selectMeteredKeys } from "services/api/pricing/consts";
import { PricingKeyRow } from "services/api/pricing/types";
import { estimateTenantSubscriptionAPI } from "services/api/subscriptions/api";
import { initialSubscriptionStepForm } from "services/api/subscriptions/consts";
import {
  SubscriptionEstimate,
  SubscriptionStepFormValues,
} from "services/api/subscriptions/types";

// UI
import { Button } from "ui/components/Button/Button";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import invoiceAnimation from "ui/theme/animations/invoice.json";

// Lokalne komponente
//
// Iste komponente koje čarobnjak koristi pri unosu, ne njihove preslike: iznos se ovdje
// prikazuje istim rasporedom i istim pravilima kao ondje gdje se pretplata ugovara.
import SubscriptionEstimateInputs from "ui/modules/tenants/pages/TenantsSearchPage/components/TenantEditCard/components/TenantSubscriptionStep/components/SubscriptionEstimateInputs/SubscriptionEstimateInputs";
import SubscriptionEstimatePanel from "ui/modules/tenants/pages/TenantsSearchPage/components/TenantEditCard/components/TenantSubscriptionStep/components/SubscriptionEstimatePanel/SubscriptionEstimatePanel";

// Tipovi
import { SubscriptionCalculatorModalProps } from "./types";

/**
 * Kalkulator iznosa: koliko bi klijent plaćao pri drugom opsegu.
 *
 * Na ekranu obračuna stoje stvarne brojke i ondje se ništa ne dira. Ovo je zaseban alat u
 * kojem se broj vozila i mjesečne količine slobodno mijenjaju, pa se vidi iznos za opseg koji
 * klijent tek planira. Ništa se ne sprema i pretplata ostaje netaknuta.
 *
 * Paketi, način plaćanja i cjenovna grupa uzimaju se iz potpisane pretplate — mijenja se samo
 * opseg, jer bi inače ispao iznos po uvjetima koje klijent nema.
 */
const SubscriptionCalculatorModal: React.FC<SubscriptionCalculatorModalProps> = (
  props: SubscriptionCalculatorModalProps
) => {
  const { show, tenantId, subscription, vehicleCount, onClose } = props;

  const [keys, setKeys] = useState<PricingKeyRow[]>([]);
  const [formValues, setFormValues] = useState<SubscriptionStepFormValues>(
    initialSubscriptionStepForm
  );
  const [estimate, setEstimate] = useState<SubscriptionEstimate | null>(null);
  const [isEstimating, setIsEstimating] = useState<boolean>(false);

  const meteredKeys = useMemo(() => selectMeteredKeys(keys), [keys]);

  const meteredNames = useMemo(
    () => Object.fromEntries(meteredKeys.map((key) => [key.code, key.name])),
    [meteredKeys]
  );

  const fetchKeys = useCallback(async () => {
    try {
      setKeys(await getPricingKeysAPI());
    } catch {
      // poruka ide kroz snackbar
    }
  }, []);

  useEffect(() => {
    if (!show) return;

    fetchKeys();
  }, [show, fetchKeys]);

  /*
    Modal ostaje montiran, pa se svako otvaranje mora vratiti na stvarno stanje. Bez toga bi
    ostale brojke iz prethodnog računanja i izgledale kao da su klijentove.
  */
  useEffect(() => {
    if (!show) return;

    setFormValues({
      ...initialSubscriptionStepForm,
      packCodes: subscription.packCodes,
      billingPeriod: subscription.billingPeriod,
      startsOn: subscription.startsOn,
      withTrial: subscription.trialEndsOn !== null,
      estimatedVehicles: vehicleCount,
    });
  }, [show, subscription, vehicleCount]);

  /*
    Nijedan iznos se ne računa ovdje. Backend vrti isti resolver i isti kalkulator kao pri
    potpisu, samo s brojkama koje su upravo upisane.
  */
  const fetchEstimate = useCallback(async () => {
    if (!show) return;

    setIsEstimating(true);

    try {
      setEstimate(await estimateTenantSubscriptionAPI(tenantId, formValues));
    } catch {
      // poruka ide kroz snackbar; radije prazno nego pogrešan iznos
      setEstimate(null);
    }

    setIsEstimating(false);
  }, [show, tenantId, formValues]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const handleVehicleCountChange = useCallback((value: number) => {
    setFormValues((prev) => ({ ...prev, estimatedVehicles: value }));
  }, []);

  const handleQuantityChange = useCallback((keyCode: string, value: number) => {
    setFormValues((prev) => ({
      ...prev,
      estimatedQuantities: { ...prev.estimatedQuantities, [keyCode]: value },
    }));
  }, []);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="full"
      position="side"
      intro={{
        content: (
          <ModalIntro
            animation={invoiceAnimation}
            title="Izračunajte iznos"
            description="Upišite broj vozila i očekivane mjesečne količine pa vidite koliko bi klijent plaćao. Računa se po njegovim paketima i cjeniku. Ništa se ne sprema."
            show={show}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title="Kalkulator pretplate"
        onHide={onClose}
      />

      <ModalBody fixedFooterSpacing={false}>
        <div className="flex items-start justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
          <div className="w-full max-w-4xl">
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Izračun iznosa
            </h1>
            <p className="text-center text-sm text-slate-600 mb-6">
              Izračun za opseg koji upišete. Pretplata se ne mijenja.
            </p>

            <div className="mb-6">
              <SubscriptionEstimateInputs
                vehicleCount={formValues.estimatedVehicles}
                meteredKeys={meteredKeys}
                quantities={formValues.estimatedQuantities}
                onVehicleCountChange={handleVehicleCountChange}
                onQuantityChange={handleQuantityChange}
              />
            </div>

            <SubscriptionEstimatePanel
              estimate={estimate}
              meteredNames={meteredNames}
              isLoading={isEstimating}
            />
          </div>
        </div>
      </ModalBody>

      <div className="flex justify-end gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50">
        <Button variant="outlined" children="Zatvori" onClick={onClose} />
      </div>
    </Modal>
  );
};

export default memo(SubscriptionCalculatorModal);
